import { IntlShape, MessageDescriptor } from 'react-intl';
import type { SpotifyCanvasFileRefusal } from 'fym-shared/src/music24/spotifyCanvas';
import { CanvasAction, CanvasChip, CanvasSentence } from './spotifyCanvasRowRules';
import { spotifyCanvasMessages as m } from './spotifyCanvasMessages';

/** The chip tones (design spotify-canvas README "Chips"): by, wt, bad, the new rv and au. */
export type CanvasChipTone = 'ok' | 'work' | 'bad' | 'review' | 'outline';

const CHIPS: Record<CanvasChip, { message: MessageDescriptor; tone: CanvasChipTone }> = {
  onSpotify: { message: m.chipOnSpotify, tone: 'ok' },
  making: { message: m.chipMaking, tone: 'work' },
  uploading: { message: m.chipUploading, tone: 'work' },
  waits: { message: m.chipWaits, tone: 'work' },
  failed: { message: m.chipFailed, tone: 'bad' },
  signedOut: { message: m.chipSignedOut, tone: 'bad' },
  inReview: { message: m.chipInReview, tone: 'review' },
  hasCanvas: { message: m.chipHasCanvas, tone: 'review' },
  auto: { message: m.chipAuto, tone: 'outline' },
};

export function canvasChipText(intl: IntlShape, chip: CanvasChip): string {
  return intl.formatMessage(CHIPS[chip].message);
}

export function canvasChipTone(chip: CanvasChip): CanvasChipTone {
  return CHIPS[chip].tone;
}

export const CANVAS_ACTION_LABELS: Record<CanvasAction, MessageDescriptor> = {
  connect: m.actionConnect,
  reconnect: m.actionReconnect,
  makeCanvas: m.actionMakeCanvas,
  makeNewCanvas: m.actionMakeNewCanvas,
  upload: m.actionUpload,
  uploadIt: m.actionUploadIt,
  retry: m.actionRetry,
  replace: m.actionReplace,
  open: m.actionOpen,
};

const RULE_TEXTS: Record<SpotifyCanvasFileRefusal, MessageDescriptor> = {
  type: m.ruleType,
  size: m.ruleSize,
  tooShort: m.ruleTooShort,
  tooLong: m.ruleTooLong,
  aspect: m.ruleAspect,
  tooSmall: m.ruleTooSmall,
};

/** "Spotify does not take this file: it is longer than 8 seconds." (design spotify-canvas a3). */
export function canvasFileText(intl: IntlShape, rule: SpotifyCanvasFileRefusal | null | undefined): string {
  if (rule === null || rule === undefined) {
    return intl.formatMessage(m.failFileNoRule);
  }
  return intl.formatMessage(m.failFile, { rule: intl.formatMessage(RULE_TEXTS[rule]) });
}

/** The app date of the design: "Sat, Oct 17, 2026" in English (the locale date, as the SoundCloud upload status). */
export function canvasDay(intl: IntlShape, iso: string): string {
  return intl.formatDate(iso, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

/** The second line of the row (design spotify-canvas a2, a3). */
export function canvasSentenceText(intl: IntlShape, sentence: CanvasSentence): string {
  const date = sentence.date === undefined ? '' : canvasDay(intl, sentence.date);
  switch (sentence.id) {
    case 'onSpotify':
      return intl.formatMessage(m.onSpotify, { date });
    case 'onSpotifyAuto':
      return intl.formatMessage(m.onSpotifyAuto, { date });
    case 'onSpotifyVersion':
      return intl.formatMessage(m.onSpotifyVersion, { revision: sentence.revision ?? 1, date });
    case 'failFile':
      return canvasFileText(intl, sentence.rule);
    case 'failRefused': {
      const detail = sentence.detail?.trim() ?? '';
      return detail === '' ? intl.formatMessage(m.failRefusedNoDetail) : intl.formatMessage(m.failRefused, { detail });
    }
    case 'notConnected':
    case 'reconnect':
    case 'noCanvas':
    case 'rendering':
    case 'noTrack':
    case 'notLiveAuto':
    case 'notLive':
    case 'ready':
    case 'uploading':
    case 'inReview':
    case 'failReconnect':
    case 'failNoPermission':
    case 'failNotLive':
    case 'failRejected':
    case 'failFileGone':
    case 'failError':
    case 'existingCanvas':
      return intl.formatMessage(m[sentence.id]);
  }
}
